import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'store'
import { createSelector } from '@reduxjs/toolkit'
import _ from 'lodash'

import MultiView, { Item } from 'devextreme-react/multi-view'

import {
    DrawerTreeList,
    DrawerMenuSearch,
    DrawerObjectsList,
    // DrawerMenuItem,
} from 'layout'
import { Tabs, Tab } from 'components/tabs'
import { DrawerState } from 'helpers/drawer-state-enum'
import { check } from 'components/can'
import { useIxAuth } from 'contexts/auth-context'
import { RootState } from 'slices'
import { toggleDrawer, setDrawerTabIndex, setDrawerWidth } from 'slices/ui'
import { setNavBadge } from 'slices/nav-badges-slice'
import { SettingType } from 'helpers/setting-type-enum'
import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'

import { useUserSettingsUpdate } from 'hooks/use-user-settings-update'

import { IIxNavItem, navItems } from 'routes'

import { LogoImg } from 'components'

import { filters } from 'filters-data'

import './drawer-menu.scss'

const menuClasses: any = {
    [DrawerState.Open]: 'drawerMenu-open',
    [DrawerState.Compact]: 'drawerMenu-compact',
    [DrawerState.Closed]: 'drawerMenu-closed',
}

const minWidth = 170

const tabs: Tab[] = [
    { name: 'Menu', icon: 'hierarchy' },
    { name: 'Favorites', icon: 'favorites' },
    { name: 'List of objects', icon: 'home' },
    { name: 'Filters', icon: 'filter' }
]

export const filterNavItems = (items: IIxNavItem[], rights: any): IIxNavItem[] => {
    const checkPerm = (x: any) => {
        const hasAccess = check(x.prg, rights)

        if (hasAccess) {
            if (x.items) {
                const checkedItems = x.items.map(checkPerm).filter((y: any) => y !== null)
                if (checkedItems.length === 0) { return null }
                else { return { ...x, items: checkedItems } }
            } else {
                return { ...x }
            }
        } else {
            return null
        }
    }

    return items
    .map(checkPerm).filter((x: IIxNavItem | null) => x !== null)  // show only accessible routes
}

const drawerStateSelector = createSelector(
    [
        (state: RootState) => state.ui.drawerState,
        (state: RootState) => state.ui.drawerTabIndex,
        (state: RootState) => state.ui.drawerWidth,
        (state: RootState) => state.ui.drawerFilter,
    ],
    (drawerState, drawerTabIndex, drawerWidth, drawerFilter) => ({
        drawerState,
        drawerTabIndex,
        drawerWidth,
        drawerFilter,
    })
)

export const DrawerMenu = () => {
    const { user } = useIxAuth()

    const dispatch = useAppDispatch()

    const [menuSearchValue, setMenuSearchValue] = useState<string>('')
    const [objSearchValue, setObjSearchValue] = useState<string>('')

    const [mainMenuItems, setMainMenuItems] = useState<IIxNavItem[]>([])
    const [favMenuItems, _setFavMenuItems] = useState<IIxNavItem[]>([])

    const updateFavoriteNavs = React.useRef(true)
    const setFavMenuItems = useCallback((items: React.SetStateAction<IIxNavItem[]>, updateSetting=true) => {
        _setFavMenuItems(items)
        updateFavoriteNavs.current = Boolean(updateSetting)
    }, [])

    const { drawerState, drawerTabIndex, drawerWidth, drawerFilter } = useSelector(drawerStateSelector)
    const rights = useSelector((state: RootState) => state.rights)

    const {value: favoriteNavs, id: favoriteNavsId} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.favoriteNavs))
    const setSettingData = useUserSettingsUpdate()

    useEffect(() => {
        const onResize = _.throttle((e: any) => {
            if (e.target.innerWidth < 800) { dispatch(toggleDrawer(DrawerState.Closed)) }
        }, 100)

        window.addEventListener('resize', onResize)
        return () => { window.removeEventListener('resize', onResize) }
    }, [dispatch])

    const setSearchValue = useCallback((value: string) => {
        if (drawerTabIndex < 2) {
            setMenuSearchValue(value)
        } else {
            setObjSearchValue(value)
        }
    }, [drawerTabIndex])

    /* v8 ignore start */
    // useEffect(() => {
    //     const fId = setInterval(() => {
    //         dispatch(setNavBadge({ path: '/issues/add', val: Math.floor(Math.random() * 50) }))
    //     }, 10 * 1000)

    //     const sId = setInterval(() => {
    //         dispatch(setNavBadge({ path: '/issues/db', val: Math.floor(Math.random() * 50) }))
    //     }, 11 * 1000)

    //     return () => {
    //         clearInterval(fId)
    //         clearInterval(sId)
    //     }
    // }, [dispatch])
    /* v8 ignore end */

    const removeInactivePage = useCallback((p: any) => {
        if (p.items) {
            const filteredItems = p.items.filter(removeInactivePage)
            p.items = filteredItems
        }

        return !p.path.startsWith('/inactive')
    },[])

    useEffect(() => {
        const filteredNavItems = navItems.filter((item: any) => (item.path !== '#Test#' && item.path !== '#Unsorted#' && item.path !== '#iX-Haus plus#' ))
        let filteredNav = filterNavItems(filteredNavItems, rights)

        setMainMenuItems(filteredNav)

        // Helper to recursively collect favorites:
        // Only include items that are favorite and either are modules or are folders and have at least one favorite module
        const collectFavorites = (items: IIxNavItem[]): IIxNavItem[] => {
            let favs: IIxNavItem[] = [];
            for (const item of items) {
                let subModules: IIxNavItem[] = [];
                if (item.items && Array.isArray(item.items)) {
                    subModules = collectFavorites(item.items);
                }
                const isFolder = item.items && item.items.length > 0;
                // if ((!isFolder && item.favorite) || (isFolder && subModules.length > 0)) {
                if ((!isFolder && favoriteNavs.indexOf(item.fullPath as any) !== -1) || (isFolder && subModules.length > 0)) {
                    // Either a favortite module or a folder containig favorite modules
                    favs.push({ ...item, items: subModules.length > 0 ? subModules : undefined });
                }
            }
            return favs;
        };

        if (favoriteNavsId) {
            const favs = collectFavorites(filteredNav)
            setFavMenuItems((items) => _.isEqual(items, favs) ? items : favs, false);
        } else {
            setFavMenuItems([], false)
        }
    }, [rights, removeInactivePage, favoriteNavs, favoriteNavsId, setFavMenuItems])

    useEffect(() => {
        if (!user || !updateFavoriteNavs.current) return

        const favFullPaths: any[] = []
        const cb = (x: any) => {
            if (x.items) { x.items.forEach(cb) }
            else { favFullPaths.push(x.fullPath) }
        }

        favMenuItems.forEach(cb)

        setSettingData('menu-config', 'favoriteNavs', favFullPaths, favoriteNavsId, SettingType.GlobalSetting)

    }, [favMenuItems, favoriteNavsId, setSettingData])

    const onFilterNav = useCallback((e: any) => {
        const value = e.event?.currentTarget.value

        if (value === '#active') {
            const filteredNavItems = filterNavItems(navItems, rights).filter(removeInactivePage)
            const filteredNav = filterNavItems(filteredNavItems, rights)
            setMainMenuItems(filteredNav)
            setMenuSearchValue('')
        }

        if (value === '#all') {
            const filteredNav = filterNavItems(navItems, rights)
            setMainMenuItems(filteredNav)
            setMenuSearchValue('')
        }

        if (value === '#hidetest') {
            const filteredNavItems = mainMenuItems.filter((item: any) => (item.path !== '#Test#' && item.path !== '#Unsorted#' ))
            const filteredNav = filterNavItems(filteredNavItems, rights)
            setMainMenuItems(filteredNav)
            setMenuSearchValue('')
        }

    },[mainMenuItems, rights, removeInactivePage])

    const onTabClick = useCallback((tabIndex: number) => {
        if (drawerState !== DrawerState.Open && tabIndex === drawerTabIndex) {
            dispatch(setDrawerTabIndex(drawerTabIndex + 1 >= (!drawerFilter ? tabs.length-1 : tabs.length) ? 0 : drawerTabIndex + 1))
        } else {
            dispatch(setDrawerTabIndex(tabIndex))
        }
    }, [dispatch, drawerState, drawerTabIndex, drawerFilter])

    const menuChanged = useCallback((e: any) => {
        if (e.name === 'selectedIndex') {
            dispatch(setDrawerTabIndex(e.value))
        }
    }, [dispatch])

    useEffect(() => {
        const handleMenuChange = (e: any) => {
            // check if event comes from input element and ignore it so we don's swicth menu state while typing
            if (e.target && (/textarea|select/i.test(e.target.nodeName) || e.target.type === "text")) { return }

            // else handle the keys if they match any shortcuts
            if (e.which === 74) { dispatch(setDrawerTabIndex(drawerTabIndex + 1 >= tabs.length ? 0 : drawerTabIndex + 1)) }
            else if (e.which === 75) { dispatch(setDrawerTabIndex(drawerTabIndex - 1 < 0 ? tabs.length - 1 : drawerTabIndex - 1)) }
        }

        document.addEventListener('keyup', handleMenuChange)

        return () => {
            document.removeEventListener('keyup', handleMenuChange)
        }
    }, [drawerTabIndex, dispatch])

    const initDrag = useCallback((e: any) => {
        e.preventDefault()
        const startPos = { x: e.clientX, width: drawerWidth }

        const doDrag = _.throttle((e: any) => {
            e.preventDefault()
            const newWidth = Math.round(startPos.width + e.clientX - startPos.x)
            // setWidth(Math.max(newWidth, minWidth))
            dispatch(setDrawerWidth(newWidth))
        }, 20)

        const stopDrag = (e: any) => {
            e.preventDefault()
            document.documentElement.removeEventListener('mousemove', doDrag)
            document.documentElement.removeEventListener('mouseup', stopDrag)

            const newWidth = Math.round(startPos.width + e.clientX - startPos.x)
            if (newWidth < minWidth) {
                dispatch(toggleDrawer(DrawerState.Closed))
                dispatch(setDrawerWidth(minWidth))
            }
        }

        document.documentElement.addEventListener('mousemove', doDrag)
        document.documentElement.addEventListener('mouseup', stopDrag)
    }, [drawerWidth, dispatch])

    const FilterComponent = useMemo(() => {
        return filters[drawerFilter] || null
    }, [drawerFilter])

    const _activeTabIndex = useMemo(() => {
        if (!FilterComponent && drawerTabIndex === 3) {
            return 0
        }
        return drawerTabIndex
    }, [FilterComponent, drawerTabIndex])

    return (
        <div id='drawerMenu' className={`drawer ${menuClasses[drawerState]}`}
            style={{width: drawerState === DrawerState.Open ? drawerWidth : 'auto', top: drawerState === DrawerState.Closed ? '62px' : 'initial'}}
            data-testid={drawerState === DrawerState.Open ? drawerWidth : 'auto'}
        >
            <DrawerMenuSearch
                onSearchValueChanged={setSearchValue}
                value={drawerTabIndex < 2 ? menuSearchValue : objSearchValue}
                onChange={onFilterNav}
            />

            <Tabs tabs={FilterComponent ? tabs : tabs.slice(0, 3)} onTabClick={onTabClick} activeTabIndex={_activeTabIndex} />

            <MultiView
                selectedIndex={_activeTabIndex}
                swipeEnabled={true}
                focusStateEnabled={false}
                onOptionChanged={menuChanged}
                deferRendering={false}
                style={{flex: '1', minHeight: 0}}>

                <Item>
                    <DrawerTreeList name='mainMenu' data={mainMenuItems} searchValue={menuSearchValue} setFavMenuItems={setFavMenuItems}/>
                </Item>

                <Item>
                    <DrawerTreeList name='favMenu' data={favMenuItems} searchValue={menuSearchValue} setFavMenuItems={setFavMenuItems}/>
                </Item>

                <Item>
                    <DrawerObjectsList searchValue={objSearchValue} />
                </Item>

                <Item disabled={!drawerFilter}>
                    {
                        FilterComponent && <FilterComponent />
                    }
                </Item>

            </MultiView>

            <LogoImg drawerState={drawerState}/>

            { /* handler for resizing */ }
            <div
                data-testid='resize'
                className='drawerMenu--resize'
                onMouseDown={initDrag}
                style={{ display: drawerState === DrawerState.Open ? 'block' : 'none' }}
            />
        </div>
    )
}
